import React from 'react';
import { Link, useLocation } from 'react-router-dom';
import { ChevronRight, Home } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { cn } from '@/lib/utils';
import { rtlMirrorHorizontalChevron } from '@/lib/locale-direction';
import { useBreadcrumbOverrides } from '@/lib/breadcrumb-overrides';

const SEGMENT_LABELS: Record<string, string> = {
  dashboard: 'breadcrumb.dashboard',
  projects: 'breadcrumb.projects',
  eduflow: 'breadcrumb.eduflow',
  learners: 'breadcrumb.learners',
  analytics: 'breadcrumb.analytics',
  videos: 'breadcrumb.videos',
  sounds: 'breadcrumb.sounds',
  images: 'breadcrumb.images',
  documents: 'breadcrumb.documents',
  store: 'breadcrumb.store',
  help: 'breadcrumb.help',
  admin: 'breadcrumb.admin',
  settings: 'breadcrumb.settings',
  profile: 'breadcrumb.profile',
  groups: 'breadcrumb.groups',
  webhooks: 'breadcrumb.webhooks',
  campaigns: 'breadcrumb.campaigns',
  competencies: 'breadcrumb.competencies',
  compliance: 'breadcrumb.compliance',
  reports: 'breadcrumb.reports',
  budget: 'breadcrumb.budget',
  catalog: 'breadcrumb.catalog',
  integrations: 'breadcrumb.integrations',
  requests: 'breadcrumb.requests',
  team: 'breadcrumb.team',
  reviews: 'breadcrumb.reviews',
  training: 'breadcrumb.training',
  skills: 'breadcrumb.skills',
  certificates: 'breadcrumb.certificates',
  new: 'breadcrumb.new',
  edit: 'breadcrumb.edit',
  podcasts: 'breadcrumb.podcasts',
  episodes: 'breadcrumb.episodes',
  'producer-assignments': 'breadcrumb.producerAssignments',
};

function humanize(segment: string): string {
  return segment
    .replace(/-/g, ' ')
    .replace(/\b\w/g, (c) => c.toUpperCase());
}

const UUID_SEGMENT_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

type Crumb = { path: string; label: string; isLast: boolean };

function buildSimpleFormationCrumbs(
  pathname: string,
  overrides: Map<string, string>,
  t: (key: string, fallback: string) => string,
): Crumb[] | null {
  const structureMatch = pathname.match(/^\/eduflow\/simple\/([^/]+)\/structure\/?$/);
  if (structureMatch) {
    const flowId = structureMatch[1];
    const flowPath = `/eduflow/simple/${flowId}`;
    const structurePath = pathname.replace(/\/$/, '') || `/eduflow/simple/${flowId}/structure`;
    return [
      { path: '/eduflow', label: t('breadcrumb.eduflow', 'Formations'), isLast: false },
      {
        path: flowPath,
        label: overrides.get(flowPath) ?? t('breadcrumb.simpleFormation', 'Formation'),
        isLast: false,
      },
      {
        path: structurePath,
        label: overrides.get(structurePath) ?? t('breadcrumb.simpleStructure', 'Structure'),
        isLast: true,
      },
    ];
  }

  const chapterMatch = pathname.match(/^\/eduflow\/simple\/([^/]+)\/chapter\/([^/]+)\/?$/);
  if (chapterMatch) {
    const flowId = chapterMatch[1];
    const chapterId = chapterMatch[2];
    const flowPath = `/eduflow/simple/${flowId}`;
    const chapterPath = `/eduflow/simple/${flowId}/chapter/${chapterId}`;
    return [
      { path: '/eduflow', label: t('breadcrumb.eduflow', 'Formations'), isLast: false },
      {
        path: `${flowPath}/structure`,
        label: overrides.get(flowPath) ?? t('breadcrumb.simpleFormation', 'Formation'),
        isLast: false,
      },
      {
        path: chapterPath,
        label: overrides.get(chapterPath) ?? t('breadcrumb.simpleChapter', 'Chapitre'),
        isLast: true,
      },
    ];
  }

  return null;
}

function buildDefaultCrumbs(
  pathname: string,
  overrides: Map<string, string>,
  t: (key: string, fallback: string) => string,
): Crumb[] {
  const segments = pathname.split('/').filter(Boolean);
  return segments
    .map((segment, index) => {
      if (UUID_SEGMENT_RE.test(segment)) return null;
      const path = '/' + segments.slice(0, index + 1).join('/');
      const override = overrides.get(path);
      const labelKey = SEGMENT_LABELS[segment];
      const label = override ?? (labelKey ? t(labelKey, humanize(segment)) : humanize(segment));
      const isLast = index === segments.length - 1;
      return { path, label, isLast };
    })
    .filter((crumb): crumb is Crumb => crumb != null)
    .map((crumb, index, list) => ({ ...crumb, isLast: index === list.length - 1 }));
}

export function Breadcrumb() {
  const { t } = useTranslation();
  const location = useLocation();
  const overrides = useBreadcrumbOverrides();

  const pathname = location.pathname.replace(/\/$/, '') || '/';
  const segments = pathname.split('/').filter(Boolean);

  if (segments.length === 0) return null;

  const crumbs =
    buildSimpleFormationCrumbs(pathname, overrides, t)
    ?? buildDefaultCrumbs(pathname, overrides, t);

  if (crumbs.length === 0) return null;

  return (
    <nav aria-label={t('breadcrumb.label', 'Breadcrumb')} className="flex items-center">
      <ol className="flex items-center gap-1 text-sm">
        <li>
          <Link
            to="/dashboard"
            className="flex items-center text-[#5B6472]/70 dark:text-[#B6CAEC] hover:text-[#5B6472] dark:hover:text-[#5B6472]/50 transition-colors"
            aria-label={t('breadcrumb.home', 'Home')}
          >
            <Home className="h-4 w-4" />
          </Link>
        </li>

        {crumbs.map((crumb) => (
          <li key={crumb.path} className="flex items-center gap-1">
            <ChevronRight
              className={cn('h-3.5 w-3.5 text-[#5B6472]/50 dark:text-[#B6CAEC]', rtlMirrorHorizontalChevron)}
              aria-hidden="true"
            />
            {crumb.isLast ? (
              <span
                className="font-medium text-[#121826] dark:text-gray-200 truncate max-w-[200px]"
                aria-current="page"
              >
                {crumb.label}
              </span>
            ) : (
              <Link
                to={crumb.path}
                className={cn(
                  'text-[#5B6472] dark:text-[#B6CAEC] hover:text-[#121826] dark:hover:text-gray-200 transition-colors truncate max-w-[150px]'
                )}
              >
                {crumb.label}
              </Link>
            )}
          </li>
        ))}
      </ol>
    </nav>
  );
}

export default Breadcrumb;
